// ClaudeCode: Auto-lock hook — locks vault on idle, tab close, screen lock
// Per vault concept spec: aggressive defaults, resist long timeouts

import { useEffect, useRef, useCallback } from 'react';

const DEFAULT_IDLE_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes (per spec suggestion)

export function useAutoLock(
  isUnlocked: boolean,
  onLock: () => void,
  idleTimeoutMs = DEFAULT_IDLE_TIMEOUT_MS
) {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const resetTimer = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (isUnlocked) {
      timerRef.current = setTimeout(() => {
        onLock();
      }, idleTimeoutMs);
    }
  }, [isUnlocked, onLock, idleTimeoutMs]);

  useEffect(() => {
    if (!isUnlocked) return;

    // Reset on user activity
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    events.forEach((e) => document.addEventListener(e, resetTimer));

    // Lock on visibility change (tab hidden, screen lock)
    const handleVisibility = () => {
      if (document.hidden) onLock();
    };
    document.addEventListener('visibilitychange', handleVisibility);

    // Lock on beforeunload (tab close)
    const handleUnload = () => onLock();
    window.addEventListener('beforeunload', handleUnload);

    // Start the idle timer
    resetTimer();

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      events.forEach((e) => document.removeEventListener(e, resetTimer));
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('beforeunload', handleUnload);
    };
  }, [isUnlocked, onLock, resetTimer]);
}
